/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectorRef, Component, Inject, NgZone} from '@angular/core';
import template from './political-entity-edit-page.component.html';
import {AppEventBusService} from '../common/events/app-event-bus.service';

import {AdminResourceEditPageBase} from '../common/controller/admin-resource-edit-page-base';
import {TeamResource} from '../../components/api/resources/resource/team.resource';
import {PoliticalEntityResource} from '../../components/api/resources/resource/political-entity.resource';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {UserProfileSaverService} from '../../components/session/user-profile-saver.service';
import {deferMarkForCheck} from '../common/util/defer-mark-for-check';

const POLITICAL_ENTITY_QUERY_PAGE_SIZE = 2000;

function isPresent(value: any): boolean {
  return value !== null && value !== undefined;
}

function unwrapDomainRef(value: any): any {
  if (value && typeof value === 'object' && Object.prototype.hasOwnProperty.call(value, 'id')) {
    return value.id;
  }
  return value;
}

function stashSponsorParentForCrumbs(m: any): void {
  if (m.type !== 'SPONSOR') {
    delete m._politicalEntityCrumbParent;
    return;
  }
  const parentRaw = isPresent(m.parent) ? m.parent : m.parentId;
  if (parentRaw && typeof parentRaw === 'object' && parentRaw.id) {
    m._politicalEntityCrumbParent = {id: parentRaw.id, name: parentRaw.name};
  } else if (isPresent(parentRaw) && parentRaw !== '') {
    m._politicalEntityCrumbParent = {id: unwrapDomainRef(parentRaw), name: ''};
  } else {
    delete m._politicalEntityCrumbParent;
  }
}

function normaliseEntityRefs(m: any): void {
  stashSponsorParentForCrumbs(m);
  m.ownerTeam = unwrapDomainRef(isPresent(m.ownerTeam) ? m.ownerTeam : m.ownerTeamId);
  m.parent = unwrapDomainRef(isPresent(m.parent) ? m.parent : m.parentId);
  delete m.ownerTeamId;
  delete m.parentId;
}

@Component({
  standalone: false,
  selector: 'adn-political-entity-edit-page',
  template
})
export class PoliticalEntityEditPageComponent extends AdminResourceEditPageBase {
  readonly entityTypes = ['CONTROLLER', 'SPONSOR', 'PAYER'];
  readonly legalEntityTypes = ['CORPORATE_ENTITY_PUBLIC', 'CORPORATE_ENTITY_PRIVATE', 'POLITICAL_PARTY', 'INDIVIDUAL'];

  readOnly = false;
  availableTeams: any[] = [];
  availableParentEntities: any[] = [];

  constructor(
    eventBus: AppEventBusService,
    @Inject(UserProfileSaverService) UserProfileSaver: any,
    @Inject(LocalNetworkProfile) LocalNetworkProfile: any,
    @Inject(LocalUserPermissions) private readonly LocalUserPermissions: any,
    @Inject(PoliticalEntityResource) private readonly PoliticalEntity: any,
    @Inject(TeamResource) private readonly Team: any,
    ngZone: NgZone,
    private readonly cdr: ChangeDetectorRef
  ) {
    super(eventBus, UserProfileSaver, LocalNetworkProfile, ngZone);
    this.readOnly = !this.LocalUserPermissions.canEditPoliticalResources();
  }

  protected afterInitEditingModel(): void {
    this.editingModel.type = this.editingModel.type || 'CONTROLLER';
    this.editingModel.address = this.editingModel.address || {};
    normaliseEntityRefs(this.editingModel);
    this.loadTeams();
    this.loadParentEntities();
  }

  protected afterInitAfterSave(): void {
    normaliseEntityRefs(this.editingModel);
    this.loadTeams();
    this.loadParentEntities();
  }

  trackById(_: number, item: any): string {
    return item.id;
  }

  trackByValue(_: number, value: string): string {
    return value;
  }

  private loadTeams(): void {
    this.Team.query({minimal: true, pageSize: 2000}).$promise.then((page: any) => {
      // [] → results flips *ngFor on the owner-team <select> mid-CD (NG0100).
      deferMarkForCheck(this.cdr, () => {
        this.availableTeams = (page.results || []).filter((team: any) => {
          return team.type === 'CONTROLLER' || team.type === 'SPONSOR';
        });
      });
    }, () => {
      deferMarkForCheck(this.cdr, () => {
        this.availableTeams = [];
      });
    });
  }

  private loadParentEntities(): void {
    this.PoliticalEntity.query({pageSize: POLITICAL_ENTITY_QUERY_PAGE_SIZE}).$promise.then((page: any) => {
      deferMarkForCheck(this.cdr, () => {
        this.availableParentEntities = (page.results || []).filter((entity: any) => {
          return entity.type === 'CONTROLLER' && entity.id !== this.editingModel.id;
        });
      });
    }, () => {
      deferMarkForCheck(this.cdr, () => {
        this.availableParentEntities = [];
      });
    });
  }
}
